import { naturalCompare } from '../../../common/utils';

/**
 * Checks behind the integrated resources view.
 *
 * An integrated resource is how a crawled activity finds its court: either the
 * integration sends an external id, or the activity title matches the row's
 * regex. The crawl tries every regex and, when more than one matches a title,
 * matches it to none of them — so the order of the rows never matters to it, and
 * two overlapping regexes silently leave activities unmatched.
 */

export const labelOf = row => row.resourceGroupName || row.externalId || row.match || '';

// The crawl runs these with .NET's Regex; the browser's engine agrees on the
// plain patterns used here, but not on every construct, so a pattern it cannot
// compile is reported (undefined) rather than treated as matching nothing.
export const compile = pattern => {
  if (!pattern) return null;
  try {
    return new RegExp(pattern);
  } catch (error) {
    return undefined;
  }
};

const isLiteral = pattern => !!pattern && !/[\\^$.*+?()[\]{}|]/.test(pattern);

// A title the crawl really sends for this row, as far as we can tell from here:
// its match when that is plain text, otherwise its group name when its own regex
// accepts it (titles and group names are usually the same words).
const sampleTitleOf = row => {
  if (isLiteral(row.match)) return row.match;
  const regex = compile(row.match);
  return regex && row.resourceGroupName && regex.test(row.resourceGroupName) ? row.resourceGroupName : null;
};

// Rows ordered by hand first, in that order; the rest by name, numbers read as numbers.
export const byDisplayOrderThenName = (a, b) =>
  (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity) || naturalCompare(labelOf(a), labelOf(b));

export const byName = (a, b) => naturalCompare(labelOf(a), labelOf(b));

/** What is wrong with a row, each as `{ tone: 'bad' | 'warn', text }`. */
export const warningsFor = (row, rows) => {
  const position = other => `#${rows.indexOf(other) + 1}`;
  const others = rows.filter(other => other.id !== row.id);
  const warnings = [];

  // No group is fine: the activities keep the title the centre sends.
  if (row.resourceGroupId && !row.resourceGroupName) {
    warnings.push({ tone: 'bad', text: 'Resource group deleted or missing' });
  }

  // A row found by title alone with no group to show it differently changes
  // nothing: the activity looks exactly as it would without the row, and its
  // regex can still steal a title from another row.
  if (!row.externalId && row.match && !row.resourceGroupId) {
    warnings.push({ tone: 'warn', text: 'Matches by title but has no resource group: it changes nothing. Delete it.' });
  }

  if (!row.externalId && !row.match) {
    warnings.push({ tone: 'warn', text: 'No external id and no match: catches nothing' });
  }

  const regex = compile(row.match);
  if (regex === undefined) {
    warnings.push({ tone: 'warn', text: 'The browser cannot read this regex: check it' });
  }

  if (row.resourceGroupId) {
    const sameGroup = others.filter(other => other.resourceGroupId === row.resourceGroupId);
    if (sameGroup.length > 0) {
      warnings.push({ tone: 'warn', text: `Same resource group as ${sameGroup.map(position).join(', ')}` });
    }

    const sameName = others.filter(other =>
      other.resourceGroupId !== row.resourceGroupId
      && row.resourceGroupName
      && naturalCompare(other.resourceGroupName, row.resourceGroupName) === 0);
    if (sameName.length > 0) {
      warnings.push({ tone: 'warn', text: `Group name also used by ${sameName.map(position).join(', ')}` });
    }
  }

  // If this row's regex also catches a title of another row, that title belongs
  // to two rows and the crawl gives it to neither.
  if (regex) {
    others
      .map(other => ({ other, sample: sampleTitleOf(other) }))
      .filter(({ sample }) => sample && regex.test(sample))
      .forEach(({ other, sample }) => warnings.push({
        tone: 'bad',
        text: `Also catches "${sample}" of ${position(other)}: neither gets it`
      }));
  }

  const withoutProduct = (row.splits || [])
    .flatMap(split => split.resources || [])
    .filter(resource => !resource.product || resource.product === 'None');
  if (withoutProduct.length > 0) {
    warnings.push({ tone: 'warn', text: `${withoutProduct.length} resource${withoutProduct.length === 1 ? '' : 's'} without a product` });
  }

  return warnings;
};

/**
 * Asks before removing a row. Its resource group is not removed with it and,
 * once no row points to it, nothing in the admin links to it any more: so its
 * id is shown in a prompt's text field, ready to copy, rather than in a confirm.
 */
export const confirmRemoval = (label, resourceGroupId) => {
  const text = `Remove ${label || 'this integrated resource'}?\n\n`
    + 'If the integration crawls only the courts listed here, this court stops being crawled. '
    + 'Otherwise its activities stop being matched to this row.';

  if (!resourceGroupId) return confirm(text);

  return prompt(
    `${text}\n\nThe resource group stays, with its splits and resources. `
    + 'Copy its id to find it again under Resource groups:',
    resourceGroupId
  ) !== null;
};

export const matchesFilter = (row, text) => {
  const key = text.trim().toLowerCase();
  if (!key) return true;

  return [
    row.resourceGroupName,
    row.externalId,
    row.match,
    ...(row.splits || []).flatMap(split => (split.resources || []).flatMap(resource => [resource.name, resource.product]))
  ].some(value => value && String(value).toLowerCase().includes(key));
};
